import type { SupportPermission, SupportTokenResponse } from '@/services/platform/platform.http'

const STORAGE_KEY = 'taxhive.platform.support-context'
export const SUPPORT_CONTEXT_EVENT = 'taxhive-support-context-changed'

export interface ActiveSupportContext {
  accessToken: string
  sessionId: string
  targetTenantId: string
  ticket: string
  permissions: SupportPermission[]
  expiresAt: string
}

function storage(): Storage | null {
  return typeof window === 'undefined' ? null : window.sessionStorage
}

export function readSupportContext(now = new Date()): ActiveSupportContext | null {
  const raw = storage()?.getItem(STORAGE_KEY)
  if (!raw) return null
  try {
    const value = JSON.parse(raw) as ActiveSupportContext
    if (!value.accessToken || !value.sessionId || !value.targetTenantId || !value.expiresAt ||
        new Date(value.expiresAt).getTime() <= now.getTime()) {
      clearSupportContext(); return null
    }
    return value
  } catch {
    clearSupportContext(); return null
  }
}

export function activateSupportContext(response: SupportTokenResponse): ActiveSupportContext {
  const session = response.support_session
  if (!session.expires_at) throw new Error('Activated support session has no expiry')
  const context: ActiveSupportContext = {
    accessToken: response.access_token,
    sessionId: session.id,
    targetTenantId: session.target_tenant_id,
    ticket: session.case_ticket,
    permissions: session.requested_permissions,
    expiresAt: session.expires_at,
  }
  storage()?.setItem(STORAGE_KEY, JSON.stringify(context))
  window.dispatchEvent(new Event(SUPPORT_CONTEXT_EVENT))
  return context
}

export function clearSupportContext(): void {
  storage()?.removeItem(STORAGE_KEY)
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(SUPPORT_CONTEXT_EVENT))
}

/** Use only for tenant-service calls made from an active support workspace. */
export function supportAuthorizationHeaders(): Record<string, string> {
  const context = readSupportContext()
  if (!context) throw new Error('No active support session')
  return { Authorization: `Bearer ${context.accessToken}` }
}
